import { googleAnalytics } from './GoogleAnalytics';
import { supabaseAnalytics } from './SupabaseAnalytics';
import { mixpanelAnalytics } from './MixpanelAnalytics';
import { analyticsConfig, AnalyticsEvent, ErrorEvent, SearchEvent, PlaylistEvent, ShareEvent } from '../../config/analytics.config';

export class AnalyticsService {
  private static instance: AnalyticsService;
  private initialized = false;
  private consentGiven = false;

  private constructor() {
    // Check for existing consent or auto-consent if not required
    this.consentGiven = analyticsConfig.privacy.requireConsent 
      ? this.checkConsent() 
      : true; // Auto-consent when not required
  }

  static getInstance(): AnalyticsService {
    if (!AnalyticsService.instance) {
      AnalyticsService.instance = new AnalyticsService();
    }
    return AnalyticsService.instance;
  }

  // Initialize analytics services
  initialize(): void {
    if (this.initialized) return;

    // Only initialize if consent is given
    if (this.consentGiven) {
      googleAnalytics.initialize();
      mixpanelAnalytics.initialize();
      // Supabase initializes itself

      // Retry any failed events from previous sessions
      supabaseAnalytics.retryFailedEvents();
    }

    this.initialized = true;
  }

  // Set user consent
  setConsent(consent: boolean): void {
    this.consentGiven = consent;
    
    // Store consent
    if (typeof window !== 'undefined') {
      localStorage.setItem('festprep_analytics_consent', consent ? 'true' : 'false');
    }
    
    // Initialize if consent given and not already initialized
    if (consent && !this.initialized) {
      this.initialize();
    }
  }

  // Check for existing consent
  private checkConsent(): boolean {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem('festprep_analytics_consent') === 'true';
  }

  // Get consent status
  hasConsent(): boolean {
    return this.consentGiven;
  }

  // Track page view
  trackPageView(path: string, title?: string): void {
    if (!this.consentGiven) return;

    googleAnalytics.trackPageView(path, title);
    mixpanelAnalytics.trackPageView(path, title);

    // Also track as event in Supabase for complete analytics
    supabaseAnalytics.trackEvent({
      name: 'page_view',
      parameters: {
        path,
        title: title || document.title,
        referrer: document.referrer
      }
    });
  }

  // Track search
  trackSearch(searchEvent: SearchEvent): void {
    if (!this.consentGiven) return;

    // Track in all services
    googleAnalytics.trackSearch(searchEvent);
    mixpanelAnalytics.trackSearch(searchEvent);
    supabaseAnalytics.trackSearch(searchEvent);
  }

  // Track playlist
  trackPlaylist(playlistEvent: PlaylistEvent): void {
    if (!this.consentGiven) return;

    // Track in all services
    googleAnalytics.trackPlaylist(playlistEvent);
    mixpanelAnalytics.trackPlaylist(playlistEvent);
    supabaseAnalytics.trackPlaylist(playlistEvent);
  }

  // Track share
  trackShare(shareEvent: ShareEvent): void {
    if (!this.consentGiven) return;

    // Track in all services
    googleAnalytics.trackShare(shareEvent);
    mixpanelAnalytics.trackShare(shareEvent);
    supabaseAnalytics.trackShare(shareEvent);
  }

  // Track generic event
  trackEvent(event: AnalyticsEvent): void {
    if (!this.consentGiven) return;

    // Track in all services
    googleAnalytics.trackEvent(event);
    mixpanelAnalytics.trackEvent(event);
    supabaseAnalytics.trackEvent(event);
  }

  // Track error (always track errors, even without consent, but anonymized)
  trackError(errorEvent: ErrorEvent): void {
    // Anonymize if no consent
    if (!this.consentGiven) {
      errorEvent.context = {
        ...errorEvent.context,
        anonymized: true
      };
    }

    // Track in all services
    googleAnalytics.trackError(errorEvent);
    mixpanelAnalytics.trackError(errorEvent);
    supabaseAnalytics.trackError(errorEvent);
  }

  // Track playlist creation failure (specific Mixpanel event)
  trackPlaylistCreationFailed(artistName: string, errorType: string): void {
    if (!this.consentGiven) return;

    mixpanelAnalytics.trackPlaylistCreationFailed(artistName, errorType);

    // Also track as generic error in other services
    this.trackError({
      error: new Error(`Playlist creation failed: ${errorType}`),
      context: { artistName, errorType },
      severity: 'medium'
    });
  }

  // Track timing
  trackTiming(category: string, variable: string, value: number, label?: string): void {
    if (!this.consentGiven) return;
    
    googleAnalytics.trackTiming(category, variable, value, label);
    
    // Also track as event in Supabase
    supabaseAnalytics.trackEvent({
      name: 'timing',
      parameters: {
        category,
        variable,
        value,
        label
      }
    });
  }

  // Set user ID (anonymized)
  setUserId(userId: string): void {
    if (!this.consentGiven) return;
    
    // Hash the user ID for privacy
    const hashedId = this.hashUserId(userId);
    googleAnalytics.setUserId(hashedId);
  }

  // Simple hash function for user ID
  private hashUserId(userId: string): string {
    let hash = 0;
    for (let i = 0; i < userId.length; i++) {
      const char = userId.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash).toString(36);
  }

  // Flush all pending events (call on app unmount)
  async flush(): Promise<void> {
    await supabaseAnalytics.flush();
  }

  // Get session ID
  getSessionId(): string {
    return googleAnalytics.getSessionId();
  }
}

// Export singleton instance
export const analytics = AnalyticsService.getInstance();

// Export types for convenience
export type { AnalyticsEvent, ErrorEvent, SearchEvent, PlaylistEvent, ShareEvent } from '../../config/analytics.config';